import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useQueryState } from "nuqs";
import { Box, Button, InputAdornment, Link, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { getUserRoleName, User } from "features/auth/types/user";
import PendingUsers from "./PendingUsers";
import Spinner from "features/spinner/Spinner";
import PageLayout from "components/PageLayout";
import OutlinedCard from "components/OutlinedCard";
import PageTitle from "components/PageTitle";
import DataTable, { type ColumnOptions } from "components/DataTable";
import { useGetAllUsersQuery } from "app/api/users";
import UpsertUserDialog from "dialogs/UpsertUserDialog";

const columns: ColumnOptions<User> = [
  {
    key: "userName",
    label: "Namn",
    renderCell: (row) => (
      <Link component={RouterLink} to={`/users/view/${row.id}`}>
        {row.userName}
      </Link>
    ),
  },
  {
    key: "email",
    label: "E-post",
  },
  {
    key: "role",
    label: "Roll",
    align: "right",
    renderCell: (row) => getUserRoleName(row.role),
  },
];

const UsersPage: React.FC = () => {
  const { data: users, isLoading } = useGetAllUsersQuery();
  const [showDialog, setShowDialog] = useState(false);
  const [searchString, setSearchString] = useQueryState("search");

  const filteredUsers = useMemo(() => {
    if (!users) return [];
    if (!searchString) return users;

    return users.filter(
      (user) =>
        user.userName.toLowerCase().includes(searchString.toLowerCase()) ||
        user.email.toLowerCase().includes(searchString.toLowerCase()) ||
        getUserRoleName(user.role).toLowerCase().includes(searchString.toLowerCase()),
    );
  }, [users, searchString]);

  return (
    <PageLayout>
      <PageTitle>Användare</PageTitle>

      <OutlinedCard>
        <Box display="flex" justifyContent="space-between" alignItems="start">
          <TextField
            value={searchString}
            onChange={(e) => setSearchString(e.target.value)}
            sx={{ width: "100%", maxWidth: "30ch" }}
            variant="filled"
            label="Sök namn, e-post, roll"
            type="search"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
          />

          <Button variant="contained" onClick={() => setShowDialog(true)}>
            Skapa ny användare
          </Button>
        </Box>

        <PendingUsers users={users ?? []} />

        {isLoading ? (
          <Box sx={{ display: "grid", placeItems: "center", gap: 2, my: 5 }}>
            <Spinner size="3rem" color="#5f5f5f" />
            <Typography>Hämtar användare</Typography>
          </Box>
        ) : (
          <DataTable data={filteredUsers} columns={columns} getKey={(row) => row.id} striped />
        )}

        {showDialog && <UpsertUserDialog create onClose={() => setShowDialog(false)} />}
      </OutlinedCard>
    </PageLayout>
  );
};

export default UsersPage;
